import { useSeo } from '@/composables/useSeo'
import { useAppStore } from '@/stores/app'
import type { AnyCalculatorDefinition } from '@/types/calculator'

/**
 * Registers a calculator page's SEO head tags, including a JSON-LD graph with
 * a BreadcrumbList (Home → Calculators → calculator), a WebApplication node for
 * the tool itself, a WebPage node carrying the latest data review date as
 * `dateModified`, and the calculator's FAQPage.
 */
export function useCalculatorSeo(definition: AnyCalculatorDefinition) {
  const app = useAppStore()
  const siteUrl = app.siteUrl.replace(/\/$/, '')
  const pageUrl = `${siteUrl}/${definition.slug}`

  // Latest data review across the calculator's sources — used as dateModified
  // so tax-year pages advertise their freshness to search engines.
  const lastReviewed = definition.assumptions
    .map((source) => source.reviewedAt)
    .filter(Boolean)
    .sort()
    .at(-1)

  useSeo({
    title: definition.seo.title,
    description: definition.seo.description,
    path: `/${definition.slug}`,
    keywords: definition.seo.keywords,
    jsonLd: [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Calculators',
            item: `${siteUrl}/calculators`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: definition.shortTitle || definition.title,
            item: pageUrl,
          },
        ],
      },
      {
        '@type': 'WebPage',
        name: definition.seo.title,
        url: pageUrl,
        description: definition.seo.description,
        inLanguage: 'en-ZA',
        ...(lastReviewed ? { dateModified: lastReviewed } : {}),
      },
      {
        '@type': 'WebApplication',
        name: definition.title,
        url: pageUrl,
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Any',
        browserRequirements: 'Requires JavaScript',
        inLanguage: 'en-ZA',
        offers: { '@type': 'Offer', price: 0, priceCurrency: 'ZAR' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: definition.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  })
}
